import { FbaBatch, FbaShipment, PlacementOption } from "@/contexts/ShipmentContext";
import { AmazonAddress, FbaBatchStatus, FBAFreightClasses, FbaShipmentStatus, PrepInstructionV2, ProductCondition, SkuGenerationMode, TransportType } from "@boxem/core-api-schema";
import { Offer } from "@/api/products";
import { Address } from "@/contexts/UserContext";
import { logRocketError } from "@/utils/logUtils";
import { Product } from "@/contexts/InventoryContext";
import { SkuFormula, SkuFormulaType } from "../settings/variable_sku/variable_sku";
import { LocalStorageKeys, getLocalStorageItem } from "@/utils/localStorageUtils";

export interface DimensionsAndWeight {
  length: number;
  width: number;
  height: number;
  weight: number;
}

export interface ListPriceOption {
  price: string;
  fba?: boolean;
  condition?: string;
  isBuyBox?: boolean;
  isAmazon?: boolean;
};

export function getDefaultDimensionsAndWeight(): DimensionsAndWeight {
  // default value is the most common dimensions/weight according to our prod db as of 2024-12-12
  const lastUsed = getLocalStorageItem(LocalStorageKeys.lastUsedDimensionsAndWeight, { length: 22, width: 18, height: 18, weight: 40 })
  // somewhere it seems that strings are being saved instead of numbers, so here we ensure the right types.
  return {
    length: Number(lastUsed.length),
    width: Number(lastUsed.width),
    height: Number(lastUsed.height),
    weight: Number(lastUsed.weight),
  };
}

// these statuses, not done yet though). these statuses are the ones we show explicitly.
export function shipmentHasShippedStatusOrLater(shipmentType: TransportType, shipmentStatus: FbaShipmentStatus, batchStatus?: FbaBatchStatus): boolean {
  switch (shipmentStatus) {    
    case FbaShipmentStatus.WORKING:
      // @NOTE(nick) in LTL we get to step 5+ with status === 'WORKING' and batch status of 'INBOUND_TRANSPORT_OPTION_CONFIRMED'
      if (shipmentType === TransportType.LTL && batchStatus !== FbaBatchStatus.INBOUND_TRANSPORT_OPTION_CONFIRMED) {
        return false;
      }
    case FbaShipmentStatus.SHIPPED:
    case FbaShipmentStatus.CLOSED:
    case FbaShipmentStatus.IN_TRANSIT:
    case FbaShipmentStatus.CHECKED_IN:
    case FbaShipmentStatus.DELIVERED:
    case FbaShipmentStatus.ERROR: // @TODO(nick) are we sure about this one?
    case FbaShipmentStatus.RECEIVING:
    case FbaShipmentStatus.READY_TO_SHIP:

    // @TODO I think we should merge the presentation of all these:
    // they all feel like 'cancelling' the shipment to the user
    case FbaShipmentStatus.CANCELLED:
    case FbaShipmentStatus.TRANSPORT_VOIDING:
    case FbaShipmentStatus.TRANSPORT_VOIDED:
      return true;
    }

  return false;
}

export function shipmentStatusDescription(shipmentType: TransportType, shipmentStatus: FbaShipmentStatus): string {
  switch (shipmentStatus) {
    case FbaShipmentStatus.WORKING:       return "The shipment was created, but has not yet shipped";
    case FbaShipmentStatus.READY_TO_SHIP: return "Labels have been printed (at least once), but has not yet shipped";
    case FbaShipmentStatus.SHIPPED:       return "The shipment was picked up by the carrier";
    case FbaShipmentStatus.RECEIVING:     return "The shipment has arrived at the fulfillment center, but not all items have been marked as received";
    case FbaShipmentStatus.CANCELLED:     return "The shipment was cancelled by the seller"; // @TODO not sure if this is always exactly true: (comes from sPAPI docs) after the shipment was sent to the fulfillment center";
    case FbaShipmentStatus.DECLINED:      return "The shipment was cancelled by the seller before the shipment was sent to the  fulfillment center";
    case FbaShipmentStatus.CLOSED:        return "The shipment has arrived at the fulfillment center and all items have been marked as received";
    case FbaShipmentStatus.ERROR:         return "There was an error with the shipment and it was not processed by Amazon";
    case FbaShipmentStatus.IN_TRANSIT:    return "The carrier has notified the fulfillment center that it is aware of the shipment";
    case FbaShipmentStatus.DELIVERED:     return "The shipment was delivered by the carrier to the fulfillment center";
    case FbaShipmentStatus.CHECKED_IN:    return "The shipment was checked-in at the receiving dock of the fulfillment center";
  
    default:
      return "";
  }
}

type MinimalFbaBatch = {
  status: FbaBatch["status"];
  shipmentType: FbaBatch["shipmentType"];
  placementOptionsStatusesFromIncompleteBatchesDTO?: FbaBatch["placementOptionsStatusesFromIncompleteBatchesDTO"];
  placementOptions?: FbaBatch["placementOptions"];
};

export function getBatchStep(batch?: MinimalFbaBatch): number | null {
  if (!batch) {
    return null;
  }

  const {
    status: batchStatus,
    shipmentType,
    placementOptionsStatusesFromIncompleteBatchesDTO,
    placementOptions = [],
  } = batch;

  const placementOptionsStatuses = placementOptionsStatusesFromIncompleteBatchesDTO ?? placementOptions.map(po => po.status);

  // you're in step 4 and onward if you've confirmed a placement option for SPD:
  // (this has to be done first because you can get 'INBOUND_XXX' statuses
  // for a bit after confirming a placement option.
  if (shipmentType === TransportType.SPD && placementOptionsStatuses.find(po => po === "ACCEPTED")) {
    if (batchStatus === FbaBatchStatus.INBOUND_TRANSPORT_OPTION_CONFIRMED) {
      // if transportation options have been confirmed, the batch/shipments are completed
      return 4;
    }
    if (batchStatus === FbaBatchStatus.BATCH_CONFIRM_ERROR || batchStatus === FbaBatchStatus.INBOUND_SHIPMENT_OPTION_LOADED) {
      // if any error happens, allow user to reconfirm the placement option and transportation option
      return 2;
    }
    return 3;
  }

  if (shipmentType === TransportType.LTL && batchStatus === FbaBatchStatus.INBOUND_TRANSPORT_OPTION_CONFIRMED) {
    // if you've confirmed the transport for LTL, you're in step 5+
    return 4;
  }

  switch (batchStatus) {
    case FbaBatchStatus.CHOOSE_PRODUCTS:
    case FbaBatchStatus.CREATING_INBOUND_PLAN:
    case FbaBatchStatus.INBOUND_PLAN_ERROR: // there was an error submitting.
      return 0; // step 1

    case FbaBatchStatus.INBOUND_PACKING_OPTION_FETCHED:
    case FbaBatchStatus.CREATING_INBOUND_PLACEMENT_OPTION:
    case FbaBatchStatus.INBOUND_PLACEMENT_OPTION_CREATED:
      if (shipmentType === TransportType.LTL) {
        // the above are all statuses we get polling while submitting items for LTL + generating placement options.
        // these occur on step 1 for LTL.
        return 0;
      }

    case FbaBatchStatus.INBOUND_PACKING_OPTION_ERROR: // so the customer has the option to re-submit.
    case FbaBatchStatus.INBOUND_PLAN_CREATED:
    case FbaBatchStatus.INBOUND_BOX_SUBMITTED:
    case FbaBatchStatus.INBOUND_BOX_PROCESSED:
    case FbaBatchStatus.INBOUND_BOX_ERROR:
      
    case FbaBatchStatus.INBOUND_PLACEMENT_OPTION_CREATED:
    case FbaBatchStatus.CREATING_INBOUND_PLACEMENT_OPTION_ERROR:
    case FbaBatchStatus.INBOUND_PLACEMENT_OPTION_ERROR:
      // step 3/2 are flipped for LTL:
      if (shipmentType === TransportType.LTL) {
        if (batchStatus === FbaBatchStatus.CREATING_INBOUND_PLACEMENT_OPTION_ERROR) {
          return 0;
        }
        if (batchStatus === FbaBatchStatus.INBOUND_PLACEMENT_OPTION_ERROR) {
          return 1;
        }
        return 2;
      }
      return 1;

    case FbaBatchStatus.INBOUND_TRANSPORT_OPTION_FETCHED:  
    case FbaBatchStatus.INBOUND_TRANSPORT_OPTION_CONFIRMED:
    case FbaBatchStatus.CONFIRMING_TRANSPORT_OPTION:
    case FbaBatchStatus.INBOUND_TRANSPORT_OPTION_CONFIRM_ERROR:
    case FbaBatchStatus.BOL_FETCHED:
      if (shipmentType === TransportType.LTL) {
        return 3;
      }
      return 2;

    case FbaBatchStatus.INBOUND_SHIPMENT_OPTION_LOADED: // when you just get to step 3
      if (shipmentType === TransportType.LTL) {
        return 1;
      }

    // these are the phases of confirming the placement option and transport option:
    case FbaBatchStatus.CONFIRMING_BATCH:
    case FbaBatchStatus.BATCH_PLACEMENT_OPTION_CONFIRMED:
    case FbaBatchStatus.INBOUND_PLACEMENT_OPTION_CONFIRMED:
      if (shipmentType === TransportType.LTL) {
        return 2;
      }
    case FbaBatchStatus.CONFIRMING_BATCH_TRANSPORTATION_OPTION:
    case FbaBatchStatus.BATCH_CONFIRM_ERROR:
      // step 3/2 are flipped for LTL:
      if (shipmentType === TransportType.LTL) {
        return 1;
      }
      return 2;

    case FbaBatchStatus.BATCH_CANCELLED:
    default: // default for the 'future' statuses:
      return 3;
  }
}


export function statusIsLoadingKind(status: FbaShipmentStatus): boolean { // @DEPRECATED REMOVE in v2
  switch (status) {
    // this should be the complete list of 'limbo' states, where a shipment should rarely be 'left' in,
    // where we expect to be polling for something to be done in order to advance the state of the shipment.
    case FbaShipmentStatus.CARTON_SUBMITTED:
    case FbaShipmentStatus.CARTON_PROCESSED:
    case FbaShipmentStatus.TRANSPORT_ESTIMATING:
    case FbaShipmentStatus.TRANSPORT_CONFIRMING:
      return true;

    default:
      return false;
  }
}

export function generateListPriceOptions(offers: Offer[]): ListPriceOption[] {
  const listPrices: ListPriceOption[] = [];
  for (let i = 0; i < offers.length; i++) {
    const offer = offers[i];
    const listPrice = {
      price: offer.ListingPrice.Amount.toString(),
      fba: offer.IsFulfilledByAmazon,
      isBuyBox: offer.IsBuyBoxWinner,
      condition: offer.SubCondition,
      isAmazon: offer.SellerId === "A2R2RITDJNW1Q6",
    };
    // we don't add duplicate list prices.
    if (
      !listPrices.find((lp) => {
        return (
          lp.price == listPrice.price &&
          lp.fba == listPrice.fba &&
          lp.isBuyBox == listPrice.isBuyBox &&
          lp.condition == listPrice.condition &&
          lp.isAmazon == listPrice.isAmazon
        );
      })
    ) {
      listPrices.push(listPrice);
    }
  }

  listPrices.sort((a, b) => {
    // the 1000x is an artificial factor to push FBA prices to the top of the sorted list.
    // there's probably a smarter way to do this, but it shouldn't produce false positives,
    // because it doesn't make sense for an FBM price to be hundreds of times more expensive
    // than any FBA listing - prices are all generally within 10-30% of each other.

    const aPrice = parseFloat(a.price);
    const bPrice = parseFloat(b.price);
    return aPrice * (a.fba ? 1 : 1000) - bPrice * (b.fba ? 1 : 1000);
  });

  return listPrices;
};

// @TODO(nick) more than 4 colors (to be safe, maybe we make ~10)
export function packingGroupToColor(packingGroupIndex: number): string | null {
  const colors = [
    "#FF5823",
    "#46B560",
    "#CBAA27",
    "#5823FF"
  ];
  return colors[packingGroupIndex];
}

export function amazonAddressToStringSingleLine(address: AmazonAddress): string {
  let addressValue = "";

  if (!!address?.addressLine1)        addressValue = addressValue + `${address.addressLine1}, `;
  if (!!address?.addressLine2)        addressValue = addressValue + `${address.addressLine2}, `;
  if (!!address?.city)                addressValue = addressValue + `${address.city} `;
  if (!!address?.stateOrProvinceCode) addressValue = addressValue + `${address.stateOrProvinceCode} `;
  if (!!address?.postalCode)          addressValue = addressValue + `${address.postalCode}`;

  return addressValue;
};

export function toAmazonAddress(a: Address): AmazonAddress {
  const amz: AmazonAddress = {
    name: a.name,
    email: a.email,
    // country: a.country,
    addressLine1: a.address1,
    addressLine2: a.address2,
    postalCode: a.zipcode,
    phoneNumber: a.phone,
    stateOrProvinceCode: a.state,
    companyName: a.company,
    city: a.city,
  };
  return amz;
}

export type FreightClass<K extends typeof FBAFreightClasses[number] = typeof FBAFreightClasses[number]> = {
  description: string;
  value: K;
  maxDensity: number;
};

export type FreightClasses = {
  [K in typeof FBAFreightClasses[number]]: FreightClass<K>;
};

export const FreightClasses: FreightClasses = {
  NONE: { value: "NONE", description: "None", maxDensity: Number.NEGATIVE_INFINITY },
  FC_500: { value: "FC_500", description: "Class 500 (Less than 1 lb per Cubic Foot)", maxDensity: 1 },
  FC_400: { value: "FC_400", description: "Class 400 (1–2 lbs per Cubic Foot)", maxDensity: 2 },
  FC_300: { value: "FC_300", description: "Class 300 (2–3 lbs per Cubic Foot)", maxDensity: 3 },
  FC_200: { value: "FC_200", description: "Class 250 (3–4 lbs per Cubic Foot)", maxDensity: 4 },
  FC_250: { value: "FC_250", description: "Class 200 (4–5 lbs per Cubic Foot)", maxDensity: 5 },
  FC_175: { value: "FC_175", description: "Class 175 (5–6 lbs per Cubic Foot)", maxDensity: 6 },
  FC_150: { value: "FC_150", description: "Class 150 (6–7 lbs per Cubic Foot)", maxDensity: 7 },
  FC_125: { value: "FC_125", description: "Class 125 (7–8 lbs per Cubic Foot)", maxDensity: 8 },
  FC_110: { value: "FC_110", description: "Class 110 (8–9 lbs per Cubic Foot)", maxDensity: 9 },
  FC_100: { value: "FC_100", description: "Class 100 (9–10.5 lbs per Cubic Foot)", maxDensity: 10.5 },
  FC_92_5: { value: "FC_92_5", description: "Class 92.5 (10.5–12 lbs per Cubic Foot)", maxDensity: 12 },
  FC_85: { value: "FC_85", description: "Class 85 (12–13.5 lbs per Cubic Foot)", maxDensity: 13.5 },
  FC_77_5: { value: "FC_77_5", description: "Class 77.5 (13.5–15 lbs per Cubic Foot)", maxDensity: 15 },
  FC_70: { value: "FC_70", description: "Class 70 (15–22.5 lbs per Cubic Foot)", maxDensity: 22.5 },
  FC_65: { value: "FC_65", description: "Class 65 (22.5–30 lbs per Cubic Foot)", maxDensity: 30 },
  FC_60: { value: "FC_60", description: "Class 60 (30–35 lbs per Cubic Foot)", maxDensity: 35 },
  FC_55: { value: "FC_55", description: "Class 55 (35–50 lbs per Cubic Foot)", maxDensity: 50 },
  FC_50: { value: "FC_50", description: "Class 50 (Over 50 lbs per Cubic Foot)", maxDensity: Number.POSITIVE_INFINITY },
};

export function estimateFreightClass(shipment: FbaShipment): { value: typeof FBAFreightClasses[number], description: string } {
  if (!shipment.pallets || shipment.pallets.length === 0 || shipment.pallets.find(p => parseFloat(p.height) === 0) !== undefined) {
    return FreightClasses.NONE;
  }

  // sum density of all the pallets:
  let totalDensities = 0;
  for (let i = 0; i < shipment.pallets.length; i++) {
    const pallet = shipment.pallets[i];
    totalDensities += parseFloat(pallet.weight) / ( parseFloat(pallet.height) * 40 * 48 / 12 / 12 / 12 ); // cubic ft.
  }
  const averageDensity = totalDensities / shipment.pallets.length;

  let index = 0;
  let freightClass;
  do {
    freightClass = FBAFreightClasses[++index];
  } while (averageDensity > FreightClasses[freightClass].maxDensity);

  return FreightClasses[freightClass];
}

export const humanReadableFbmStatus = (status: string) => {
  switch (status) {
    case "DRAFT":
      return "Draft";
    case "PROCESSING":
      return "Processing";
    case "LISTED":
      return "Successfully Listed";
    case "INACTIVE":
      return "Inactive"
    case "ERROR":
      return "Error";
    case "DELETED":
      return "Deleted";
    case "REVIEW_LISTINGS":
      return "Review Listings";
    case "CHOOSE_PRODUCTS":
      return "Choose Products"
    default:
      return "Unknown Status"
  }
};

export function relcalculateFbmListPriceHelpText(offersLoading: boolean, price: number, listPriceOptions: ListPriceOption[]): string {
  // calculate what we should display in the little 'help' text below list price:
  const buyBox = listPriceOptions.find((a) => a.isBuyBox && !a.fba);
  const buyBoxPrice = parseFloat(buyBox?.price ?? "0");
  if (buyBox) {
    if (!price || price === 0) {
      return `The FBM buy-box for this listing is: $${buyBox.price}.`;

    } else if (price === buyBoxPrice) {
      return "Your price matches the FBM buy-box price.";

    } else {
      const delta = Math.abs(100 - (price / buyBoxPrice) * 100).toFixed(1);
      return `Your price is ${delta}% ${buyBoxPrice < price ? "higher" : "lower"} than the FBM buy-box price.`;
    }
  } else if (!offersLoading) {
    return "This listing doesn't have an FBM buy-box.";
  }

  return "";
};

export function relcalculateListPriceHelpText(offersLoading: boolean, price: number, listPriceOptions: ListPriceOption[]): string {
  // calculate what we should display in the little 'help' text below list price:
  const buyBox = listPriceOptions.find((a) => a.isBuyBox && a.fba);
  const buyBoxPrice = parseFloat(buyBox?.price ?? "0");
  if (buyBox) {
    if (!price || price === 0) {
      return `The FBA buy-box for this listing is: $${buyBox.price}.`;

    } else if (price === buyBoxPrice) {
      return "Your price matches the FBA buy-box price.";

    } else {
      const delta = Math.abs(100 - (price / buyBoxPrice) * 100).toFixed(1);
      return `Your price is ${delta}% ${buyBoxPrice < price ? "higher" : "lower"} than the FBA buy-box price.`;
    }
  } else if (!offersLoading) {
    return "This listing doesn't have an FBA buy-box.";
  }

  return "";
};

export function categoryProbablyRequiresExpirationDate(categoryString: string): boolean {
  if (!categoryString || categoryString === "") return false;

  switch (categoryString) {
    case "Beauty & Personal Care":
    case "Beverages":
    case "Food":
    case "Grocery & Food": // this one was in the spread sheet but I've never seen it, though 'Grocery & Gourmet Food' is a category and wasn't in the SS. Typo?
    case "Grocery & Gourmet Food":
    case "Health, Household & Baby Care":
    case "Pet Supplies":
      return true;

    default:
      logRocketError(`Unknown product category: ${categoryString}`);

    // categories below this comment (but above the bigger block below) were discovered via the logrocket message above:
    case "Health & Household":
    case "Kitchen & Dining":
    case "Tools & Home Improvement":
    case "Automotive":
    case "CDs & Vinyl":
    case "Camera & Photo Products":
    case "Computers & Accessories":
    case "Sports Collectibles":

    //
    case "Appliances":
    case "Arts, Crafts & Sewing":
    case "Automotive Parts & Accessories":
    case "Baby":
    case "Books":
    case "Business & Industrial":
    case "Cameras & Photo":
    case "Cell Phones & Accessories":
    case "Clothing, Shoes & Jewelry":
    case "Collectibles & Fine Art":
    case "Computers":
    case "Digital Music":
    case "Electronics":
    case "Entertainment Collectibles":
    case "Furniture":
    case "Garden & Outdoor":
    case "Home & Kitchen":
    case "Industrial & Scientific":
    case "Jewelry":
    case "Kindle eBooks":
    case "Lawns & Garden":
    case "Luggage & Travel Gear":
    case "Movies & TV":
    case "Musical Instruments":
    case "Office Products":
    case "Patio, Lawn & Garden":
    case "Software":
    case "Sports & Outdoors":
    case "Tools & Hardware":
    case "Toys & Games":
    case "Video Games":
      return false;
  }
};

// makes either a valid formula or valid random SKU to autofill the SKU field
export const generateDefaultSku = (
  product: Product,
  sellerDataIndex: number,
  skuGenerationOption: SkuGenerationMode,
  formula: SkuFormula,
  allSellerSkus: Set<string>,
  skuGenerationCount: number,
) => {
  if (skuGenerationOption === SkuGenerationMode.boxemGenerated) {
    let i = 0; // to be safe, only try 10 times (could be infinite loop otherwise). 99.99+% of cases we would only run the loop once.
    while (i++ < 10) {
      const a = new Uint8Array(5); // the length of generated string is this x2. character limit for SKU is 40, but 40 overflows the box a bit, and 10 characters is enough entropy.
      window.crypto.getRandomValues(a); //@NOTE(nick) this is only available on https!!!
      const randomSku = Array.from(a, (d) => d.toString(16).padStart(2, "0"))
        .join("")
        .toUpperCase();
      if (!allSellerSkus.has(randomSku)) {
        return randomSku;
      }
    }
    return "";
  } else if (skuGenerationOption === SkuGenerationMode.variable) {
    let s = "";
    for (let i = 0; i < formula.length; i++) {
      const type = formula[i].type;
      let field = "";
      switch (type) {
        // becuase this function just gets the initial state of the input box,
        // these fields will always be blank initially.
        case SkuFormulaType.buyCost:
        case SkuFormulaType.supplier:
        case SkuFormulaType.purchaseDate:
          break;

        case SkuFormulaType.listPrice:
          // @TODO this could be filled based on autofill settings?
          break;

        case SkuFormulaType.count:
          field = skuGenerationCount.toString() ?? "0";
          break;
        case SkuFormulaType.condition:
          field = productConditionToString(product.sellerData?.[sellerDataIndex]?.condition ?? ProductCondition.New);
          break;
        case SkuFormulaType.salesRank:
          field = product.salesRank ? product.salesRank.toString() : "";
          break;
        case SkuFormulaType.asin:
          field = product.asin;
          break;
        case SkuFormulaType.custom:
          field = formula[i].string;
          break;
      }
      s += field;
      if (i !== formula.length - 1 && field !== "") s += "-";
    }
    return s.replaceAll(" ", "");
  }
  return "";
};

export function prepInstructionV2ToString(prepInstruction: PrepInstructionV2): { name: string, description: string } {
  switch (prepInstruction) {
    case "ITEM_LABELING":
      return { name: "FNSKU Labeling", description: "The FNSKU label must be applied to the item." };
    case "ITEM_BUBBLEWRAP":
      return { name: "Bubble Wrapping", description: "Bubble wrapping is required." };
    case "ITEM_POLYBAGGING":
      return { name: "Polybagging", description: "Product must be sealed in a Polybag." };
    case "ITEM_TAPING":
      return { name: "Taping", description: "Taping is required." };
    case "ITEM_BLACK_SHRINKWRAP":
      return { name: "Black Shrink Wrapping", description: "Black shrink wrapping is required." };
    case "ITEM_HANG_GARMENT":
      return { name: "Hang Garment", description: "The product should be placed on a hanger." };
    case "ITEM_BOXING":
      return { name: "Boxing", description: "Products may require overboxing when there are safety concerns over sharp items, fragile items, hazardous liquids, and vinyl records. For items over 4.5 kg, use double-wall corrugated boxes." };
    case "ITEM_SETCREAT":
      return { name: "Set Creation", description: "Products that are sets must be labeled as sets on their packaging. The barcodes on the individual products must 1) not face outward and 2) not require covering." };
    case "ITEM_RMOVHANG":
      return { name: "No Hanger", description: "The product must not be shipped on a hanger. Remove the hanger, if present, before shipping." };
    case "ITEM_SUFFOSTK":
      return { name: "Suffocation Polybagging", description: "Product must be sealed in a Polybag with an opening of 12cm or larger (measured when flat). The warning must be printed on the bag or attached as a label." };
    case "ITEM_CAP_SEALING":
      return { name: "Cap Sealing", description: "To prevent leakage, product needs to have a secondary seal in one of the following types: Induction seal, safety ring, clips, heat shrink plastic band, or boxing." };
    case "ITEM_DEBUNDLE":
      return { name: "Debundling", description: "This item requires taking apart a set of items for individual sale. Remove tape or shrink wrap that groups multiple inventory units together." };
    case "ITEM_SETSTK":
      return { name: "Set Stickering", description: "Products that are sets (for example, a set of six unique toy cars that is sold as one unit) must be marked as sets on their packaging. Add a label to the unit that clearly states that the products are to be received and sold as a single unit." };
    case "ITEM_SIOC":
      return { name: "Ships in original packaging", description: "The product ships in its original packaging." };

    // these are documented as possible values, but there is no explaination anywhere I could find that explains 
    // what they mean. @NOTE actually, they appear to be the 'PrepCategory' - but they are also listed as prep instructions??
    // https://developer-docs.amazon.com/sp-api/docs/fulfillment-inbound-api-v2024-03-20-reference#prepinstruction
    // @NOTE(nick) I figured it out, the actual type of 'prepType' in the above is a union of 'PrepType' and 'PrepCategory',
    // but they wrote it just as a 'string'.
    // case "ADULT":
    // case "BABY":
    // case "TEXTILE":
    // case "HANGER":
    // case "FRAGILE":
    // case "LIQUID":
    // case "SHARP":
    // case "SMALL":
    // case "PERFORATED":
    // case "GRANULAR":
    // case "SET":
    // case "FC_PROVIDED":

    default:
    case "ITEM_NO_PREP":
      return null; // @NOTE don't change to an empty object or something, function just below relies on this being falsy.
  }
};

// there are a few values that are known to mean 'nothing' or 'unknown', we'll tell if you if this is one of them:
export function prepInstructionIsNone(prepInstruction: PrepInstructionV2): boolean {
  return !prepInstructionV2ToString(prepInstruction);
};

export const getReadableStatus = (shipment: string) => {
  switch (shipment) {
    case "BATCH_CANCELLED":
      return "Shipment Cancelled";
    case "CREATED":
      return "Created";
    case "CONFIRMED":
      return "Confirmed";
    case "READY_TO_SHIP":
      return "Ready to ship";
    case "CARTON_SUBMITTED":
      return "Carton submitted";
    case "CARTON_SUBMISSION_FAILED":
      return "Carton submission failed";
    case "CARTON_PROCESSED":
      return "Carton processed";
    case "CARTON_PROCESSED_WITH_ERROR":
      return "Carton processed with error";
    case "TRANSPORT_SUBMITTED":
      return "Transport submitted";
    case "TRANSPORT_SUBMISSION_ERROR":
      return "Transport submission error";
    case "TRANSPORT_ESTIMATING":
      return "Transport estimating";
    case "TRANSPORT_ESTIMATED":
      return "Transport estimated";
    case "TRANSPORT_ESTIMATED_ERROR":
      return "Transport estimated error";
    case "TRANSPORT_CONFIRMING":
      return "Transport confirming";
    case "TRANSPORT_CONFIRMING_ERROR":
      return "Transport confirming error";
    case "TRANSPORT_CONFIRMED":
      return "Transport confirmed";
    case "TRANSPORT_VOID_ERROR":
      return "Transport void error";
    case "TRANSPORT_VOIDED":
    case "TRANSPORT_VOIDING":
      return "Shipment voided";
    case "LABEL_ACQUIRED":
      return "Label acquired";
    case "CANCELLED":
      return "Cancelled";
    case "WORKING":
      return "Working";
    case "SHIPPED":
      return "Shipped";
    case "RECEIVING":
      return "Receiving";
    case "IN_TRANSIT":
      return "In transit";
    case "ERROR":
      return "Error";
    case "ACCEPTED":
      return "Accepted";
    case "INVALID":
      return "Invalid";
    case "DELIVERED":
      return "Delivered";
    case "DELETED":
      return "Deleted";
    case "CLOSED":
      return "Closed";
    case "CHECKED_IN":
      return "Checked In";
    case "CARTON_FEED_CREATED":
      return "Carton feed created";
    case "CARTON_DOCUMENT_UPLOADED":
      return "Carton document uploaded";
    case "CHOOSE_PRODUCTS":
      return "Choose products";
    case "REVIEW_LISTINGS":
      return "Review listings";
    case "LISTED":
      return "Successfully listed";
    case "LISTING_PROCESSING":
      return "Processing";
    default:
      return shipment;
  }
};

export function productConditionToString(c: ProductCondition, abbreviate: boolean = true): string {
  switch (c) {
    case ProductCondition.New:
      return "New";
    case ProductCondition.NewOpenBox:
      return "New - Open Box";
    case ProductCondition.NewOEM:
      return "New - OEM";

    case ProductCondition.Refurbished:
      return "Refurbished";

    case ProductCondition.UsedLikeNew:
      return "Used - Like New";
    case ProductCondition.UsedVeryGood:
      return "Used - Very Good";
    case ProductCondition.UsedGood:
      return "Used - Good";
    case ProductCondition.UsedAcceptable:
      return "Used - Acceptable";
  
    case ProductCondition.CollectibleLikeNew:
      if (abbreviate) return "Coll. - Like New";
      return "Collectible - Like New";
    case ProductCondition.CollectibleVeryGood:
      if (abbreviate) return "Coll. - Very Good";
      return "Collectible - Very Good";
    case ProductCondition.CollectibleGood:
      if (abbreviate) return "Coll. - Good";
      return "Collectible - Good";
    case ProductCondition.CollectibleAcceptable:
      if (abbreviate) return "Coll. - Acceptable"; // this is the worst-case for label layout (most text)
      return "Collectible - Acceptable";

    case ProductCondition.Refurbished:
      return "Refurbished";

    case ProductCondition.Club:
      return "Club";

    default:
      if (c !== null && c !== undefined) {
        logRocketError(`Unknown product condition received on frontend! Enum value is: |${c}|`)
      } 
      return "";
  }
}
